/** Loads the complete DataView contract needed by lists and dashboard drilldowns. */
import {useCallback, useMemo} from "react";
import {ApolloError} from "@apollo/client";
import {DataViewTypeEnum, useDataViewColumnsQuery} from "../../Queries";
import {DataViewAnnotations, parseDataViewAnnotations} from "./DataViewColumns";

export interface UseDataViewResult {
   dataViewId?: string;
   dataViewType?: DataViewTypeEnum;
   expiresAt?: Date;
   viewName?: string;
   rowIds: string[];
   annotations: DataViewAnnotations | null;
   loading: boolean;
   error?: ApolloError;
   retry: () => Promise<void>;
}

/**
 * Reads a DataView and exposes safe defaults while it is skipped or loading.
 */
export const useDataView = (viewId?: string): UseDataViewResult => {
   const {data, loading, error, refetch} = useDataViewColumnsQuery({
      variables: {
         viewId
      },
      skip: !viewId
   });

   const retry = useCallback(async (): Promise<void> => {
      await refetch();
   }, [refetch]);

   return useMemo(() => ({
      dataViewId: typeof data?.DataView?._id === "string" ? data.DataView._id : undefined,
      dataViewType: data?.DataView?.type,
      expiresAt: data?.DataView?.expiresAt || undefined,
      viewName: data?.DataView?.viewName || undefined,
      rowIds: (data?.DataView?.rowIds || []).filter((id): id is string => typeof id === "string"),
      annotations: parseDataViewAnnotations(data?.DataView?.metadata),
      loading,
      error,
      retry
   }), [data, error, loading, retry]);
};
